// Data-driven route pages for SEO. Add a new route = add one entry here.
// Slugs should be `${from-slug}-to-${to-slug}`.

const commonBenefits = [
  {
    title: "Save up to 60% on your travel cost",
    desc: "Split fuel, tolls, and parking with your Trip Host or Co-Traveller. A ride that would cost you a lot solo can drop by more than half when three people share.",
  },
  {
    title: "Verified travellers, verified vehicles",
    desc: "Every Trip Host on RideMate is ID-verified. Vehicles are checked for valid RC and Driving Licence. You know exactly who you're riding with.",
  },
  {
    title: "Flexible schedules",
    desc: "Unlike buses or trains, you leave when you want. Pick a Trip Host whose timing matches yours — no fixed stations, no early morning waits.",
  },
  {
    title: "Door-to-door convenience",
    desc: "Most Trip Hosts pick you up close to your home and drop you close to your destination. No auto-rickshaw hunt at odd hours.",
  },
  {
    title: "Greener commute",
    desc: "Every shared seat takes one car off the road. Less fuel, less traffic, less pollution — a small win for your city.",
  },
  {
    title: "24×7 in-app support",
    desc: "Ratings, in-app chat, live location during trip, and human support if anything goes wrong.",
  },
];

const howItWorks = [
  {
    n: "01",
    t: "Sign up in under 2 minutes",
    d: "Verify your phone and identity. One RideMate profile for both Co-Traveller and Trip Host roles.",
  },
  {
    n: "02",
    t: "Find a trip or post one",
    d: "Search rides between your two cities, filter by date/time, and book a seat. Driving yourself? Post your trip and offset the fuel.",
  },
  {
    n: "03",
    t: "Ride, split, save",
    d: "Meet at the pickup point, share the ride, and pay directly through the app. Rate each other after the trip.",
  },
];

export const ROUTES = {
  "singrauli-to-varanasi": {
    slug: "singrauli-to-varanasi",
    from: "Singrauli",
    to: "Varanasi",
    fromState: "Madhya Pradesh",
    toState: "Uttar Pradesh",
    distanceKm: 230,
    durationHours: "5–6",
    typicalFare: "₹500 – ₹700 per seat",
    solo: "₹1,800 – ₹2,500",
    tagline: "Singrauli to Varanasi Carpool & Ride Sharing",
    heroBlurb:
      "Book a shared ride from Singrauli to Varanasi with RideMate — verified Trip Hosts, split-cost model, and comfortable cars going your way every day. Share the fuel, save your time, ride together.",
    intro:
      "Travelling between Singrauli and Varanasi is a common weekly trip for students, working professionals, and families. Buses can be crowded and rigid on timing; trains are often booked out; and a private cab is an expensive full-fare commitment. RideMate makes this route affordable, flexible, and social — you share a car with someone already driving the same way and split the travel cost.",
    highlights: [
      "Direct rides via NH-135A / Renukoot & Chunar corridor",
      "Popular pickup points: Waidhan, Morwa, NCL colonies",
      "Popular drop-offs: Lanka, BHU, Sigra, Cantt, Airport",
      "Great for daily and weekly commuters",
    ],
    faqs: [
      {
        q: "How much time does a Singrauli to Varanasi ride take on RideMate?",
        a: "Most Trip Hosts complete the Singrauli to Varanasi journey in 5–6 hours depending on traffic, weather, and how many pickups are on the way. A direct non-stop private ride can be closer to 5 hours.",
      },
      {
        q: "How is the fare shared on the Singrauli–Varanasi route?",
        a: "RideMate uses a split-cost model — the trip's fuel and travel expenses are divided fairly among all co-travellers in the car. The exact per-seat share depends on vehicle type, seats booked, and pickup/drop-off points, and is shown transparently inside the app before you confirm the ride.",
      },
      {
        q: "Can I take luggage on a shared ride?",
        a: "Yes. Standard passenger luggage (one medium suitcase and a backpack) is allowed. If you have extra bags, message the Trip Host in advance so they can plan the boot space.",
      },
      {
        q: "Do I have to travel with strangers?",
        a: "Every Trip Host and Co-Traveller on RideMate is ID-verified. You can see the driver profile, ratings and reviews, and vehicle details before booking. You can also chat with them inside the app.",
      },
      {
        q: "Can I book a return ride from Varanasi to Singrauli?",
        a: "Yes. Once you complete a booking, you can immediately search for Varanasi to Singrauli rides and pre-book your return leg the same day.",
      },
    ],
  },

  "varanasi-to-singrauli": {
    slug: "varanasi-to-singrauli",
    from: "Varanasi",
    to: "Singrauli",
    fromState: "Uttar Pradesh",
    toState: "Madhya Pradesh",
    distanceKm: 230,
    durationHours: "5–6",
    typicalFare: "₹500 – ₹700 per seat",
    solo: "₹1,800 – ₹2,500",
    tagline: "Varanasi to Singrauli Shared Rides",
    heroBlurb:
      "Find a comfortable, verified shared ride from Varanasi to Singrauli every day on RideMate. Pool with locals heading home — save money, meet people, ride smarter.",
    intro:
      "The Varanasi to Singrauli route is one of the busiest inter-state commutes in the eastern UP–MP belt. Students at BHU going home, coal-sector professionals heading to NCL, and families visiting relatives — RideMate connects all of them with drivers already making the trip.",
    highlights: [
      "Pickup from Lanka, BHU Gate, Cantt Station, Sigra, Airport",
      "Direct route via Chunar, Robertsganj, Renukoot corridor",
      "Same-day and next-day booking, no fixed timings",
      "Trusted drivers with verified vehicles and RC",
    ],
    faqs: [
      {
        q: "How far is Varanasi from Singrauli?",
        a: "The road distance from Varanasi to Singrauli is approximately 230 km. Depending on the route taken (via Renukoot / Robertsganj), the total travel time is usually 5–6 hours on RideMate.",
      },
      {
        q: "Which is cheaper — bus, train, or RideMate carpool?",
        a: "A shared RideMate ride uses a split-cost model — you only pay a fair share of the fuel and travel cost, not a full private-cab fare. Compared to state transport buses or trains, RideMate offers more flexibility on timing, doorstep pickups, and comfort of a car.",
      },
      {
        q: "Can I book a Varanasi to Singrauli ride for late night travel?",
        a: "Late night rides depend on the availability of Trip Hosts. You can filter by time slot on RideMate and see hosts departing between 8 PM and 12 AM. Book at least a few hours in advance for late departures.",
      },
      {
        q: "Is RideMate safe for solo female travellers?",
        a: "Every Trip Host is ID-verified. Female travellers can filter for verified female hosts, see ratings, chat before boarding, and share their live location with family through the app.",
      },
      {
        q: "How do I pay for the ride?",
        a: "Payments happen securely inside the RideMate app through Razorpay — UPI, cards, and net banking are supported. There is no cash negotiation and no surprise surge.",
      },
    ],
  },

  "singrauli-to-renukoot": {
    slug: "singrauli-to-renukoot",
    from: "Singrauli",
    to: "Renukoot",
    fromState: "Madhya Pradesh",
    toState: "Uttar Pradesh",
    distanceKm: 50,
    durationHours: "1.5–2",
    typicalFare: "₹250 – ₹400 per seat",
    solo: "₹900 – ₹1,200",
    tagline: "Singrauli to Renukoot Ride Sharing",
    heroBlurb:
      "Daily shared rides from Singrauli to Renukoot on RideMate — the smart way for professionals and students to save time and split the fuel.",
    intro:
      "Singrauli and Renukoot sit close on the Sonebhadra industrial corridor. Thousands of workers, contractors, and engineers make this trip every week for Hindalco, NCL, and NTPC assignments. RideMate turns those private car trips into shared pool rides — everyone wins.",
    highlights: [
      "Pickup from Waidhan, Morwa, Vindhyanagar, Jayant",
      "Direct connection to Renukoot bus stand and Hindalco gates",
      "Daily commuter-friendly early-morning and evening rides",
      "Round-trip options for same-day return",
    ],
    faqs: [
      {
        q: "What is the distance from Singrauli to Renukoot?",
        a: "Singrauli to Renukoot is approximately 50 km by road via NH-75E. A shared ride typically takes 1.5 to 2 hours depending on traffic and pickup points.",
      },
      {
        q: "Do RideMate rides run daily on the Singrauli–Renukoot route?",
        a: "Yes. This is a commuter route with multiple Trip Hosts posting rides every morning and evening. Peak availability is between 7–9 AM and 5–7 PM.",
      },
      {
        q: "How is the fare split for Singrauli to Renukoot?",
        a: "RideMate uses a split-cost model — the trip's fuel and travel expenses are divided fairly among all co-travellers in the vehicle. Your per-seat share is shown transparently in the app before you confirm.",
      },
      {
        q: "Can I book a daily commuter pass?",
        a: "RideMate is rolling out daily and weekly commuter passes for regular routes like Singrauli–Renukoot. Join the waitlist to be the first to access these plans.",
      },
    ],
  },

  "renukoot-to-varanasi": {
    slug: "renukoot-to-varanasi",
    from: "Renukoot",
    to: "Varanasi",
    fromState: "Uttar Pradesh",
    toState: "Uttar Pradesh",
    distanceKm: 150,
    durationHours: "3–4",
    typicalFare: "₹300 – ₹450 per seat",
    solo: "₹1,000 – ₹1,400",
    tagline: "Renukoot to Varanasi Carpooling",
    heroBlurb:
      "Carpool from Renukoot to Varanasi on RideMate. Verified travellers, flexible timings, and up to 60% cheaper than a private cab.",
    intro:
      "The Renukoot to Varanasi corridor is a familiar route for students, patients heading to BHU / SS Hospital, and shoppers heading to the city. RideMate lets you skip the crowded bus stand and grab a comfortable seat in a car already driving that way.",
    highlights: [
      "Pickup from Renukoot main market, Hindalco gate, Anpara",
      "Route via Robertsganj – Chunar to Varanasi",
      "Drop-offs at BHU, Lanka, Cantt, Sigra, Nadesar",
      "Booking flexibility for weekend travel",
    ],
    faqs: [
      {
        q: "How long does the Renukoot to Varanasi ride take?",
        a: "The Renukoot to Varanasi journey is approximately 150 km and takes 3 to 4 hours depending on route, weather, and stops.",
      },
      {
        q: "What is the best time to travel from Renukoot to Varanasi?",
        a: "Most Trip Hosts on this route depart early morning (5–8 AM) for daytime work / medical trips, and evening (5–7 PM) for BHU students and returning residents. Traffic near Chunar and Varanasi entry can slow evening travel.",
      },
      {
        q: "Are there rides for BHU / IIT students?",
        a: "Yes, this is one of the most popular routes for BHU and IIT-BHU students. You can filter by drop-off location like 'BHU Main Gate' or 'IIT-BHU'.",
      },
      {
        q: "Can I split my fare with friends travelling with me?",
        a: "Yes. Book multiple seats in one booking and RideMate will let you split the total among the group via the app.",
      },
    ],
  },

  "renukoot-to-singrauli": {
    slug: "renukoot-to-singrauli",
    from: "Renukoot",
    to: "Singrauli",
    fromState: "Uttar Pradesh",
    toState: "Madhya Pradesh",
    distanceKm: 50,
    durationHours: "1.5–2",
    typicalFare: "₹250 – ₹400 per seat",
    solo: "₹900 – ₹1,200",
    tagline: "Renukoot to Singrauli Ride Sharing",
    heroBlurb:
      "Book a shared ride from Renukoot to Singrauli every day on RideMate. Verified drivers, split fuel costs, comfortable cars heading your way.",
    intro:
      "Renukoot to Singrauli is a daily commuter corridor for engineers, contractors, and residents of the Sonebhadra industrial belt. RideMate matches you with drivers already making the trip so you save both money and time.",
    highlights: [
      "Pickup from Hindalco gate, Renukoot market, Anpara",
      "Direct route via NH-75E through Sonebhadra",
      "Popular drop-offs: Waidhan, Morwa, Vindhyanagar, NCL colonies",
      "Round-trip friendly for daily commuters",
    ],
    faqs: [
      {
        q: "How far is Renukoot from Singrauli?",
        a: "Renukoot to Singrauli is approximately 50 km by road via NH-75E. A shared ride typically takes 1.5 to 2 hours.",
      },
      {
        q: "Do RideMate rides run daily on the Renukoot–Singrauli route?",
        a: "Yes. This is a busy industrial commuter route with multiple Trip Hosts posting rides every morning and evening.",
      },
      {
        q: "How is the fare split for Renukoot to Singrauli?",
        a: "RideMate uses a split-cost model — the trip's fuel and travel expenses are divided fairly among all co-travellers. Your per-seat share is shown transparently in the app before you confirm.",
      },
      {
        q: "Can I book a daily commuter pass?",
        a: "RideMate is rolling out daily and weekly commuter passes for regular routes like Renukoot–Singrauli. Join the waitlist to be first to access.",
      },
    ],
  },

  "singrauli-to-robertsganj": {
    slug: "singrauli-to-robertsganj",
    from: "Singrauli",
    to: "Robertsganj",
    fromState: "Madhya Pradesh",
    toState: "Uttar Pradesh",
    distanceKm: 180,
    durationHours: "4–5",
    typicalFare: "₹400 – ₹600 per seat",
    solo: "₹1,400 – ₹1,900",
    tagline: "Singrauli to Robertsganj Carpool & Ride Sharing",
    heroBlurb:
      "Shared rides from Singrauli to Robertsganj (Sonebhadra HQ) on RideMate — verified Trip Hosts, split-cost model, and cars heading through the Sonebhadra corridor every day.",
    intro:
      "Robertsganj is the district headquarters of Sonebhadra and a key transit town for anyone moving between Singrauli and central UP. RideMate makes this trip affordable and social — you share a car with someone already driving the same way instead of hunting through the bus stand.",
    highlights: [
      "Pickup from Waidhan, Morwa, NCL colonies, Vindhyanagar",
      "Route via NH-75E · Renukoot · Sonebhadra corridor",
      "Drop-off at Robertsganj Bus Stand, District HQ, market area",
      "Great for administrative and college visits",
    ],
    faqs: [
      {
        q: "How long does a Singrauli to Robertsganj ride take?",
        a: "The Singrauli to Robertsganj journey typically takes 4 to 5 hours depending on route conditions and pickup points. The road passes through Renukoot and Sonebhadra corridor.",
      },
      {
        q: "How is the fare split for Singrauli to Robertsganj?",
        a: "RideMate uses a split-cost model — the trip's fuel and travel expenses are divided fairly among all co-travellers in the car. Your per-seat share is shown transparently in the app before you confirm the ride.",
      },
      {
        q: "Are morning and evening rides available on this route?",
        a: "Yes. Trip Hosts commonly depart in the early morning for daytime work at district offices and colleges, and late evening for the return trip.",
      },
      {
        q: "Can I book a return ride from Robertsganj to Singrauli?",
        a: "Yes. After booking, you can search for the reverse Robertsganj to Singrauli ride and pre-book your return the same day.",
      },
    ],
  },

  "robertsganj-to-singrauli": {
    slug: "robertsganj-to-singrauli",
    from: "Robertsganj",
    to: "Singrauli",
    fromState: "Uttar Pradesh",
    toState: "Madhya Pradesh",
    distanceKm: 180,
    durationHours: "4–5",
    typicalFare: "₹400 – ₹600 per seat",
    solo: "₹1,400 – ₹1,900",
    tagline: "Robertsganj to Singrauli Shared Rides",
    heroBlurb:
      "Find a verified shared ride from Robertsganj to Singrauli on RideMate. Pool with locals heading to the NCL / Singrauli industrial belt — save money, split fuel, ride smarter.",
    intro:
      "Robertsganj to Singrauli is a common route for administrative staff, students, and Sonebhadra residents commuting to the industrial belt. RideMate connects you with Trip Hosts already making the drive so you can skip crowded buses.",
    highlights: [
      "Pickup from Robertsganj Bus Stand, District HQ, main market",
      "Direct route via Sonebhadra · Renukoot · NH-75E",
      "Drop-off at Waidhan, Morwa, NCL colonies, Vindhyanagar",
      "Same-day and next-day booking, no fixed timings",
    ],
    faqs: [
      {
        q: "How far is Robertsganj from Singrauli?",
        a: "The road distance from Robertsganj to Singrauli is approximately 180 km. Total travel time is usually 4 to 5 hours on RideMate.",
      },
      {
        q: "Which is more comfortable — bus or RideMate carpool?",
        a: "A shared RideMate ride with 2–3 co-travellers is typically more comfortable and flexible than a state transport bus, and often cheaper than a private cab. You leave when you want, pickup close to home.",
      },
      {
        q: "Is late night travel available on this route?",
        a: "Late-night rides depend on Trip Host availability. You can filter by time slot in the app and see hosts departing late evening.",
      },
      {
        q: "How do I pay for the ride?",
        a: "All payments happen inside the RideMate app via Razorpay — UPI, cards, and net banking supported. No cash negotiation, no surge pricing.",
      },
    ],
  },
};

export const ROUTE_SLUGS = Object.keys(ROUTES);
export const COMMON_BENEFITS = commonBenefits;
export const HOW_IT_WORKS = howItWorks;

/**
 * Curated bidirectional pairs to feature in the Popular Routes section.
 * Each pair links to `primarySlug` but the label shows both directions.
 */
export const POPULAR_PAIRS = [
  {
    primarySlug: "singrauli-to-varanasi",
    reverseSlug: "varanasi-to-singrauli",
    cityA: "Singrauli",
    cityB: "Varanasi",
    durationHours: "5–6",
    distanceKm: 230,
  },
  {
    primarySlug: "singrauli-to-renukoot",
    reverseSlug: "renukoot-to-singrauli",
    cityA: "Singrauli",
    cityB: "Renukoot",
    durationHours: "1.5–2",
    distanceKm: 50,
  },
  {
    primarySlug: "singrauli-to-robertsganj",
    reverseSlug: "robertsganj-to-singrauli",
    cityA: "Singrauli",
    cityB: "Robertsganj",
    durationHours: "4–5",
    distanceKm: 180,
  },
];
